import { useQuery } from '@api/hooks'
import { faBriefcaseBlank } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Box, Button, LinearProgress, Paper, Skeleton, Stack, Typography } from '@mui/material'
import MarketBasketReportsApi, { MarketBasketReportsKeys } from '@services/marketBasketReports.api'
import dayjs from 'dayjs'
import { Link } from 'react-router-dom'

export default function DirectDeals() {
	const dateRangeEnd = dayjs().format('YYYY-MM-DD')
	const dateRangeStart = dayjs().subtract(30, 'day').format('YYYY-MM-DD')

	const { data, isLoading } = useQuery({
		queryKey: MarketBasketReportsKeys.directDealOverview({ dateRangeStart, dateRangeEnd }),
		queryFn: () => MarketBasketReportsApi.directDealOverview({ dateRangeStart, dateRangeEnd }),
	})

	return (
		<DirectDealsUI
			title="Direct Deal Reporting"
			recommended={data?.recommendedTotal ?? null}
			recommendedAccepted={data?.recommendedAccepted ?? null}
			decline={data?.declineTotal ?? null}
			declineRejected={data?.declineRejected ?? null}
			isLoading={isLoading}
			subtitle="past 30 days"
		/>
	)
}

type UiProps = {
	title: string
	isLoading: boolean
	recommended: number | null
	recommendedAccepted: number | null
	decline: number | null
	declineRejected: number | null
	subtitle?: string
}
export function DirectDealsUI({
	title,
	isLoading,
	recommended,
	recommendedAccepted,
	decline,
	declineRejected,
	subtitle = '',
}: UiProps) {
	const recommendPct =
		recommended && recommendedAccepted != null ? (recommendedAccepted / recommended) * 100 : null
	const declinePct = decline && declineRejected != null ? (declineRejected / decline) * 100 : null

	return (
		<Paper elevation={1} sx={{ width: '100%', p: 3 }} data-testid="direct-deals-stats">
			<Stack spacing={4}>
				<Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
					<Stack direction="row" alignItems="center" spacing={1.5}>
						<FontAwesomeIcon icon={faBriefcaseBlank} size="lg" />
						<Typography variant="h6" color="text.primary">
							{title}
						</Typography>
						<Typography variant="body2" color="text.secondary">
							{subtitle}
						</Typography>
					</Stack>
					<Button
						component={Link}
						to="/app/market-baskets/analytics/direct-deals"
						variant="outlined"
						color="secondary"
						data-testid="direct-deals-stats-details-button"
					>
						Details
					</Button>
				</Stack>

				<Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
					{isLoading ? (
						<>
							<Skeleton variant="rounded" sx={{ flex: 1, borderRadius: 2 }} height={150} />
							<Skeleton variant="rounded" sx={{ flex: 1, borderRadius: 2 }} height={150} />
						</>
					) : (
						<>
							<Card
								label="Recommend"
								accepted={recommendedAccepted}
								total={recommended}
								percentage={recommendPct}
								color="success.main"
								link="/app/market-baskets/analytics/direct-deals?market-baskets=NOT_ACCEPTED"
							/>
							<Card
								label="Decline"
								accepted={declineRejected}
								total={decline}
								percentage={declinePct}
								color="error.main"
								link="/app/market-baskets/analytics/direct-deals?market-baskets=NOT_REJECTED"
							/>
						</>
					)}
				</Stack>
			</Stack>
		</Paper>
	)
}

type CardProps = {
	label: string
	accepted: number | null
	total: number | null
	percentage: number | null
	color: string
	link: string
}

function Card({ label, accepted, total, percentage, color, link }: CardProps) {
	return (
		<Paper
			component={Link}
			to={link}
			variant="outlined"
			data-testid={`direct-deals-stats-card-${label.toLowerCase()}`}
			sx={{
				flex: 1,
				p: 2,
				borderRadius: 2,
				borderColor: 'divider',
				boxShadow: 'none',
				cursor: 'pointer',
				textDecoration: 'none',
				color: 'inherit',
				'&:hover': {
					textDecoration: 'none',
				},
			}}
		>
			<Stack spacing={1.5}>
				<Stack direction="row" alignItems="center" spacing={1.25}>
					<Box
						sx={{
							width: 20,
							height: 20,
							borderRadius: '50%',
							bgcolor: color,
							flexShrink: 0,
						}}
					/>
					<Typography variant="body1" color="text.secondary">
						{label}
					</Typography>
				</Stack>

				<Stack direction="row" alignItems="baseline" spacing={1}>
					<Typography variant="h4" fontWeight={600}>
						{Math.round(percentage ?? 0)}%
					</Typography>
					<Typography variant="body1" color="text.secondary">
						{label === 'Recommend' ? 'Accepted' : 'Rejected'}
					</Typography>
				</Stack>

				<LinearProgress
					variant="determinate"
					value={percentage ?? 0}
					sx={{
						height: 10,
						borderRadius: 1,
						bgcolor: 'grey.300',
						'& .MuiLinearProgress-bar': {
							borderRadius: 1,
							bgcolor: color,
						},
					}}
				/>

				<Typography variant="subtitle1" color="text.primary">
					{accepted ?? 0} of {total ?? 0} deals
				</Typography>
			</Stack>
		</Paper>
	)
}
